import React, {Fragment, useState, useEffect} from 'react';
import {Layout, Icon, Icons, Flex, Button, Goals, Separator} from 'components';
import {printSidebar, redirect, useList, useNamedSelector, moment} from 'helpers';
import {forbid} from "store/session";
import css from './weekly-objectives.scss';

const groupByWeek = (objectives, currentWeek) => {
  const grouped = objectives.reduce((acc, objective) => {
    if (!objective.target_date) return acc;

    const date = moment(Number(objective.target_date) * 1000);
    const weekNumber = date.week();
    const weekYear = date.year();
    const weekKey = `${weekYear}-${weekNumber}`;

    if (!acc[weekKey]) {
      const weekStart = date.clone().startOf('week');
      const weekEnd = date.clone().endOf('week');

      acc[weekKey] = {
        weekNumber: weekNumber,
        weekStart: weekStart.format('MMM D'),
        weekEnd: weekEnd.format('MMM D'),
        year: weekYear,
        objectives: [],
        startHere: weekNumber === currentWeek,
      };
    }

    acc[weekKey].objectives.push(objective);
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) =>
    moment(`${a.year}-${a.weekNumber}`, 'YYYY-W').diff(moment(`${b.year}-${b.weekNumber}`, 'YYYY-W'))
  );
};

export function Sidebar({match: {params: {id, page = ''}}}) {
  const [ q, setSearch ] = useState('');

  const { firstDayOfWeek } = useNamedSelector('settings.settings');
  moment.updateLocale('en', {
    week: {
      dow: firstDayOfWeek - 1 // 1 = Sunday, 2 = Monday
    }
  });
  const currentWeek = moment().week();

  const weeklyObjectives = useNamedSelector('goals.weeklyObjectivesList', {
    module: 'goals',
    filter: useNamedSelector('settings.shareStatusFilter', {context: 'goals'})
  });

  const base = '/goals/weekly-objectives/'.concat(page, page ? '/' : '');

  const [ list, Last ] = useList(weeklyObjectives, {
    search: { q, keys: ['name'] },
  });

  const {premium} = useNamedSelector('session.subscription');
  const showLimitAlert = !premium && (weeklyObjectives.length >= 1);

  const groupedObjectives = groupByWeek(list, currentWeek);

  const scrollToStart = () => {
    const containerElement = document.querySelector('div[role="feed"]');
    const targetLink = containerElement?.querySelector(`p[id="start-here"]`);
    if (containerElement && targetLink) {
      const targetPosition = targetLink.offsetTop - containerElement.offsetTop;
      containerElement.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      let id = groupedObjectives.find(group => (group.weekNumber) === currentWeek).objectives[0].id;
      redirect(`/goals/weekly-objectives/${id}`, true);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToStart();
    }, 1000);
  }, []);

  return (
    <Layout.Sidebar>
      <Flex className={['secondary', 'middle']}>
        <Layout.Sidebar.Search value={q} setValue={setSearch} />
        <Icons.Print onClick={printSidebar} />
        <Goals.WeeklyObjectives.SidebarPreferences shareStatus />
        { showLimitAlert
          ?
          <Icon name="plus-large" title="Add Weekly Objective" onClick={forbid} />
          :
          <Icon name="plus-large" title="Add Weekly Objective" to={'/goals/weekly-objectives/add/'} />
        }
      </Flex>
      <Layout.Sidebar.Scrollable>
        {groupedObjectives.map(group => (
          <Fragment key={`${group.year}-${group.weekNumber}`}>
            <Separator heading={`Week ${group.weekNumber}`} id={group.startHere ? "start-here" : undefined} />
            <p className={css.weekDates}>
              {`${group.weekStart} - ${group.weekEnd}`}
            </p>
            {group.objectives.map(goal => (
              <Goals.WeeklyObjectives.Card
                id={goal.id}
                key={goal.id}
                navLink={`${base}${goal.id}/`}
                targetDate={goal.target_date}
              />
            ))}
          </Fragment>
        ))}
        <Last />
        { list.length === 0 &&
          <p className="text-center">Add weekly objectives that need to be accomplished. </p>
        }
        { showLimitAlert
          ?
          <p className="hidden-print">
            <Button icon="plus" className={['stretch', 'primary']} onClick={forbid}>
              New Weekly Objective
            </Button>
          </p>
          :
          <p className="hidden-print">
            <Button icon="plus" className={['stretch', 'primary']} to={'/goals/weekly-objectives/add/'}>
              New Weekly Objective
            </Button>
          </p>
        }
      </Layout.Sidebar.Scrollable>
    </Layout.Sidebar>
  );
}
